import React, { FC, useEffect, useState, useMemo } from 'react';
import {
  Button,
  Chip,
  IconButton,
  Skeleton,
  Box,
  Typography,
  Drawer,
  Paper,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { Table, TableBody, TableCell, TableHead, TableRow, tableCellClasses } from '@mui/material';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import CustomDataGrid from '../../components/DataGrid/DataGrid';
import { defaultRowsPerPageOptions } from '../../utils/api.types';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  WorkOutline as WorkIcon,
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  AccountBalanceWallet as SalaryIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { fetchSalaries, deleteSalary, paySalary, payDailyWage, downloadSalarySlip } from '../../store/salaryStore/salaryThunk';
import { fetchEmployees } from '../../store/employeeStore/employeeThunk';
import { ApiStatus } from '../../utils/api.types';
import { SalaryPayment } from '../../utils/salary.types';
import { Utils } from '../../utils/common-utils';
import styled_ from "@emotion/styled";
import DialogProcessSalaryForm from '../../components/Salary/DialogProcessSalaryForm';
import DialogProcessDailyWageForm from '../../components/Salary/DialogProcessDailyWageForm';
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import generatePDF, { Resolution, Margin, Options } from "react-to-pdf";

const StyledTableCell = styled_(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#fff',
    color: '#000',
    fontSize: '0.80rem',
    fontWeight: '700',
    borderRight: '2px solid #F5F5F5',
    padding: '12px 16px',
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: '0.975rem',
    fontWeight: '400',
    color: '#292d35',
    padding: '8px 16px',
    borderBottom: 'none',
  },
}));

const StyledTableRow = styled_(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: '#f9fafb',
  },
  '& > *': {
    borderBottom: '0px',
  }
}));

const SalaryList: FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { salaries, rowCount, status } = useAppSelector(state => state.salaries);
  const { selectedAccount } = useAppSelector(state => state.auth);
  const { employees } = useAppSelector(state => state.employees);

  const [openSalaryDrawer, setOpenSalaryDrawer] = useState(false);
  const [openDailyWageDrawer, setOpenDailyWageDrawer] = useState(false);
  const [isRefresh, setIsRefresh] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [salaryToDelete, setSalaryToDelete] = useState<SalaryPayment | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Pagination states
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Filter states
  const [selectedEmployee, setSelectedEmployee] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<string>(new Date().getFullYear().toString());

  useEffect(() => {
    if (selectedAccount?._id) {
      dispatch(fetchEmployees({ accountId: selectedAccount._id, type: 'all' }));
    }
  }, [dispatch, selectedAccount]);

  // The salary/daily-wage drawers are "persistent" (no modal scroll-lock), so the
  // page behind them keeps scrolling and peeks through the top band. Lock the app's
  // main scroll container while either drawer is open.
  useEffect(() => {
    const main = document.querySelector('main') as HTMLElement | null;
    if (!main) return;
    if (openSalaryDrawer || openDailyWageDrawer) {
      const prevOverflow = main.style.overflowY;
      main.style.overflowY = 'hidden';
      return () => { main.style.overflowY = prevOverflow; };
    }
  }, [openSalaryDrawer, openDailyWageDrawer]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('open') === 'process-salary') {
      setTabValue(0);
      setOpenSalaryDrawer(true);
      params.delete('open');
      navigate(
        {
          pathname: location.pathname,
          search: params.toString() ? `?${params.toString()}` : '',
        },
        { replace: true }
      );
    }
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (selectedAccount?._id) {
        const filters: any = {
          accountId: selectedAccount._id,
          paymentType: tabValue === 0 ? 'MONTHLY_SALARY' : 'DAILY_WAGE',
          page,
          limit: pageSize
        };

        if (selectedEmployee) filters.employee = selectedEmployee;

        if (tabValue === 0) {
          if (selectedMonth && selectedYear && selectedYear.length === 4) {
            filters.month = `${selectedYear}-${selectedMonth}`;
          } else if (selectedYear && selectedYear.length === 4) {
            filters.year = selectedYear;
          }
        }

        dispatch(fetchSalaries(filters));
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [dispatch, selectedAccount, isRefresh, selectedEmployee, selectedMonth, selectedYear, tabValue, page, pageSize]);

  const handleRefresh = () => {
    setIsRefresh(Date.now().toString());
  };

  const handleOpenSalary = () => setOpenSalaryDrawer(true);
  const handleOpenDailyWage = () => setOpenDailyWageDrawer(true);

  const handleCloseSalary = async (val: any) => {
    if (val) {
      handleRefresh();
      const salaryId = typeof val === 'object' ? val.salaryId : '';
      if (salaryId) {
        setOpenSalaryDrawer(false);
        navigate(`/salary/${salaryId}/view`);
        return;
      }
    }
    setOpenSalaryDrawer(false);
  };

  const handleCloseDailyWage = async (val: any) => {
    if (val) {
      handleRefresh();
    }
    setOpenDailyWageDrawer(false);
  };

  const handleDeleteSalary = async (salaryId: string) => {
    try {
      await dispatch(deleteSalary(salaryId)).unwrap();
      handleRefresh();
    } catch (error) {
      console.error('Failed to delete salary:', error);
    }
  };

  const handleMarkAsPaid = async (salary: SalaryPayment) => {
    try {
      const paymentMode = salary.paymentMode || 'CASH';

      const paymentData: any = {
        id: salary._id,
        paymentDate: new Date(),
        paymentMode: paymentMode,
      };

      // Only include fromBankAccount if payment mode is not CASH
      if (paymentMode !== 'CASH' && salary.fromBankAccount) {
        // Extract ID if fromBankAccount is a populated object
        paymentData.fromBankAccount = typeof salary.fromBankAccount === 'string'
          ? salary.fromBankAccount
          : (salary.fromBankAccount as any)._id;
      }

      // Call appropriate API based on payment type
      if (salary.paymentType === 'DAILY_WAGE') {
        await dispatch(payDailyWage(paymentData)).unwrap();
      } else {
        await dispatch(paySalary(paymentData)).unwrap();
      }

      handleRefresh();
    } catch (error) {
      console.error('Failed to mark as paid:', error);
      alert('Failed to mark as paid. Please try again.');
    }
  };

  // Delete dialog handlers
  const handleDeleteClick = (salary: SalaryPayment) => {
    setSalaryToDelete(salary);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (salaryToDelete) {
      await handleDeleteSalary(salaryToDelete._id);
    }
    setDeleteDialogOpen(false);
    setSalaryToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSalaryToDelete(null);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(0);
  };

  // Download salary slip PDF
  const handleDownloadSlip = async (salaryId: string, employeeName: string) => {
    try {
      const { blob } = await dispatch(downloadSalarySlip(salaryId)).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Salary_Slip_${employeeName.replace(/\s+/g, '_')}_${moment().format('YYYY_MM_DD')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download salary slip:', error);
      alert('Failed to download salary slip. Please try again.');
    }
  };

  // Export to Excel
  const exportToExcel = (): void => {
    const exportData = (salaries || []).map((salary: any) => ({
      Date: moment(salary.createdAt).format("DD MMM YYYY"),
      'TXN ID': salary.txnId,
      Employee: salary.employee?.name || '-',
      'Emp ID': salary.employee?.empId || '-',
      Type: salary.paymentType === 'MONTHLY_SALARY' ? 'Monthly' : 'Daily Wage',
      Period: salary.paymentType === 'MONTHLY_SALARY'
        ? (salary.paymentPeriod?.month || '-')
        : `${moment(salary.paymentPeriod?.fromDate).format('DD/MM/YY')} - ${moment(salary.paymentPeriod?.toDate).format('DD/MM/YY')}`,
      'Gross Amount': parseFloat(salary.grossSalary || 0).toFixed(2),
      Deductions: parseFloat(salary.deductions?.totalDeductions || 0).toFixed(2),
      'Net Payable': parseFloat(salary.netSalary || 0).toFixed(2),
      Status: salary.paymentStatus,
    }));

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const workbook: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Salary Data");

    const excelBuffer: any = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const dataBlob: Blob = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });

    saveAs(dataBlob, `Salary_List_${moment().format('YYYYMMDD')}.xlsx`);
  };

  // PDF export options
  const options: Options = {
    filename: `Salary_Statement_${Date.now()}.pdf`,
    method: "save",
    resolution: Resolution.NORMAL,
    page: {
      margin: Margin.MEDIUM,
      format: "A4",
    },
    canvas: {
      mimeType: "image/jpeg",
      qualityRatio: 1
    },
    overrides: {
      pdf: {
        compress: true
      },
      canvas: {
        useCORS: true
      }
    }
  };

  const getTargetElement = () => document.getElementById("pdfContainer");

  const downloadPdf = () => {
    generatePDF(getTargetElement, options);
  };

  // Common columns shared between Monthly and Daily Wage
  const commonColumnsStart: GridColDef[] = [
    {
      field: 'createdAt',
      headerName: 'Date',
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => row.createdAt ? (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ fontWeight: 500, fontSize: '9pt' }}>{moment(row.createdAt).format("DD MMM YYYY")}</span>
            <Chip label={`TXN #${row.txnId}`} size="small" style={{ fontSize: '7pt', height: '18px', backgroundColor: '#7c2d12', color: 'white' }} />
          </div>
          <span style={{ fontSize: '7pt', color: '#6b7280' }}>{moment(row.createdAt).format("hh:mm A")}</span>
        </div>
      ) : '-',
    },
    {
      field: 'employee',
      headerName: 'Employee',
      flex: 1.2,
      renderCell: ({ row }: GridValueGetterParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.3' }}>
          <span style={{ fontWeight: 500, fontSize: '9pt' }}>{row.employee?.name || '-'}</span>
          <span style={{ fontSize: '8pt', color: '#6b7280' }}>{row.employee?.empId || ''}</span>
        </div>
      ),
    },
    {
      field: 'period',
      headerName: 'Period',
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => row.paymentType === 'MONTHLY_SALARY'
        ? (row.paymentPeriod?.month || '-')
        : <span style={{ fontSize: '8pt' }}>{moment(row.paymentPeriod?.fromDate).format('DD/MM/YY')} - {moment(row.paymentPeriod?.toDate).format('DD/MM/YY')}</span>,
    },
  ];

  const commonColumnsEnd: GridColDef[] = [
    {
      field: 'netSalary',
      headerName: 'Net Payable',
      flex: 1,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }: GridValueGetterParams) => (
        <div style={{ width: '100%', textAlign: 'right' }}>
          <span style={{ fontWeight: 600 }}>{row.netSalary > 0 ? Utils.formatNumber(parseFloat(row.netSalary).toFixed(2), true, true) : '-'}</span>
          {row.expensePayments && row.expensePayments.length > 0 && (
            <div style={{ fontSize: '0.75rem', color: '#92400e', fontWeight: 500 }}>
              + Exp: {Utils.formatNumber(row.expensePayments.reduce((s: number, e: any) => s + (e.amount || 0), 0).toFixed(2), true, true)}
            </div>
          )}
        </div>
      ),
    },
    {
      field: 'paymentStatus',
      headerName: 'Status',
      flex: 0.7,
      renderCell: ({ row }: GridValueGetterParams) => (
        <Chip label={row.paymentStatus} size="small" style={{ fontSize: '7pt', height: '20px', backgroundColor: row.paymentStatus === 'PAID' ? '#16a34a' : row.paymentStatus === 'PROCESSED' ? '#ea580c' : '#6b7280', color: 'white' }} />
      ),
    },
    {
      field: 'actions',
      headerName: 'Action',
      flex: 1,
      sortable: false,
      renderCell: ({ row }: GridValueGetterParams) => (
        <>
          <IconButton size="small" onClick={() => navigate(`/salary/${row._id}/view`)} color="info" title="View Salary Slip" sx={{ mr: 0.5 }}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={() => handleDownloadSlip(row._id, row.employee?.name || 'Employee')} color="success" title="Download Salary Slip" sx={{ mr: 0.5 }}>
            <DownloadIcon fontSize="small" />
          </IconButton>
          {(row.paymentStatus === 'PENDING' || row.paymentStatus === 'PROCESSED') && (
            <IconButton size="small" onClick={() => handleMarkAsPaid(row)} color="primary" title="Mark as Paid" sx={{ mr: 0.5 }}>
              <PaymentIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton size="small" onClick={() => handleDeleteClick(row)} color="error" title="Delete">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </>
      ),
    },
  ];

  const monthlyColumns: GridColDef[] = useMemo(() => [
    ...commonColumnsStart,
    {
      field: 'grossSalary',
      headerName: 'Gross Amount',
      flex: 1,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }: GridValueGetterParams) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.grossSalary > 0 ? Utils.formatNumber(parseFloat(row.grossSalary).toFixed(2), true, true) : '-'}</div>
      ),
    },
    {
      field: 'deductions',
      headerName: 'Deductions',
      flex: 1,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }: GridValueGetterParams) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.deductions?.totalDeductions > 0 ? Utils.formatNumber(parseFloat(row.deductions.totalDeductions).toFixed(2), true, true) : '-'}</div>
      ),
    },
    ...commonColumnsEnd,
  ], []);

  const dailyWageColumns: GridColDef[] = useMemo(() => [
    ...commonColumnsStart,
    {
      field: 'rateDays',
      headerName: 'Rate/Days',
      flex: 1,
      renderCell: ({ row }: GridValueGetterParams) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '1.2' }}>
          <span style={{ fontSize: '8pt', color: '#6b7280' }}>
            {row.dailyWage?.wageCalculationMethod === 'PER_DAY'
              ? `₹${row.dailyWage?.ratePerDay || 0}/day × ${row.dailyWage?.daysWorked || 0} days`
              : `₹${row.dailyWage?.ratePerHour || 0}/hr × ${row.dailyWage?.hoursWorked || 0} hrs`
            }
          </span>
          {row.dailyWage?.overtimeHours > 0 && (
            <span style={{ fontSize: '7pt', color: '#059669' }}>
              + OT: {row.dailyWage.overtimeHours}hrs @ ₹{row.dailyWage.overtimeRate}/hr
            </span>
          )}
        </div>
      ),
    },
    {
      field: 'grossSalary',
      headerName: 'Gross Wage',
      flex: 1,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: ({ row }: GridValueGetterParams) => (
        <div style={{ width: '100%', textAlign: 'right' }}>{row.grossSalary > 0 ? Utils.formatNumber(parseFloat(row.grossSalary).toFixed(2), true, true) : '-'}</div>
      ),
    },
    ...commonColumnsEnd,
  ], []);


  return (
    <>
    <Box sx={{ width: '100%', borderBottom: 1, borderColor: 'divider', backgroundColor: '#fff' }}>
      {/* Header Bar with Actions */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
 
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#fafafa'
      }}>
      
        <Box sx={{ borderBottom: 1, borderColor: 'divider', }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="Salary Tabs">
            <Tab label="Monthly Salary" sx={{ textTransform: 'none', fontWeight: 600 }} />
            <Tab label="Daily Wages" sx={{ textTransform: 'none', fontWeight: 600 }} />
          </Tabs>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<WorkIcon />}
            onClick={handleOpenDailyWage}
            variant="outlined"
            color='secondary'
            size="small"
            sx={{ height: '36px', textTransform: 'none' }}
          >
            Process Daily Wage
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={handleOpenSalary}
            variant="contained"
            color='secondary'
            size="small"
            sx={{ height: '36px', textTransform: 'none' , mr: 2}}
          >
            Process Salary
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider',  display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="employee-filter-label" shrink={true}>Employee</InputLabel>
          <Select
            labelId="employee-filter-label"
            value={selectedEmployee}
            label="Employee"
            onChange={(e) => setSelectedEmployee(e.target.value)}
            displayEmpty
            notched={true}
          >
            <MenuItem value="">All Employees</MenuItem>
            {employees?.map((emp: any) => (
              <MenuItem key={emp._id} value={emp._id}>
                {emp.name} ({emp.empId})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Show Month and Year filters only for Monthly Salary tab */}
        {tabValue === 0 && (
          <>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel id="month-filter-label" shrink={true}>Month</InputLabel>
              <Select
                labelId="month-filter-label"
                value={selectedMonth}
                label="Month"
                onChange={(e) => setSelectedMonth(e.target.value)}
                displayEmpty
                notched={true}
              >
                <MenuItem value="">All Months</MenuItem>
                <MenuItem value="01">January</MenuItem>
                <MenuItem value="02">February</MenuItem>
                <MenuItem value="03">March</MenuItem>
                <MenuItem value="04">April</MenuItem>
                <MenuItem value="05">May</MenuItem>
                <MenuItem value="06">June</MenuItem>
                <MenuItem value="07">July</MenuItem>
                <MenuItem value="08">August</MenuItem>
                <MenuItem value="09">September</MenuItem>
                <MenuItem value="10">October</MenuItem>
                <MenuItem value="11">November</MenuItem>
                <MenuItem value="12">December</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Year"
              type="number"
              size="small"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ width: 120 }}
              inputProps={{ min: 2020, max: 2050 }}
            />
          </>
        )}

        <Button
          variant="outlined"
          size="small"
          onClick={() => {
            setSelectedEmployee('');
            setSelectedMonth('');
            setSelectedYear(new Date().getFullYear().toString());
          }}
          sx={{ height: '40px' }}
        >
          Clear Filters
        </Button>
      </Box>

      {/* Table Content */}
      <Box sx={{ p: 0, pb: 3, pr: '10px' }}>
        <div id='pdfContainer'>
          {status !== ApiStatus.LOADING && rowCount === 0 ? (
            <Paper
              elevation={0}
              sx={{
                height: '70vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fafafb',
                border: '2px dashed #e0e0e0',
                borderRadius: 2,
                p: 4
              }}
            >
              <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    borderRadius: '50%',
                    backgroundColor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    mb: 3
                  }}
                >
                  <SalaryIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                </Box>
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 600, color: '#333', mb: 1 }}
                >
                  {tabValue === 0 ? 'No Salaries Yet' : 'No Daily Wages Yet'}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: '#666', mb: 4, lineHeight: 1.6 }}
                >
                  {tabValue === 0
                    ? 'Start processing salaries for your employees. You can manage monthly payroll, track deductions, and more.'
                    : 'Start processing daily wages for your employees. You can manage daily rates, track hours, and more.'}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={tabValue === 0 ? handleOpenSalary : handleOpenDailyWage}
                  sx={{
                    px: 4,
                    py: 2,
                    borderRadius: 3,
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 600,
                    letterSpacing: '0.5px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-4px) scale(1.02)',
                      boxShadow: (theme: any) => `0 8px 25px -8px ${theme.palette.primary.main}`,
                    },
                    '&:active': {
                      transform: 'translateY(-2px) scale(0.98)',
                    },
                  }}
                >
                  {tabValue === 0 ? 'Process Salary' : 'Process Daily Wage'}
                </Button>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mt: 3, color: '#999' }}
                >
                  Need help? Check our guide on salary management
                </Typography>
              </Box>
            </Paper>
          ) : (
            <div style={{ height: '77vh', width: '100%', overflow: 'auto' }}>
              <CustomDataGrid
                columns={tabValue === 0 ? monthlyColumns : dailyWageColumns}
                rows={salaries || []}
                rowCount={rowCount}
                rowsPerPageOptions={defaultRowsPerPageOptions}
                page={page}
                pageSize={pageSize}
                onPageChange={(newPage: number) => setPage(newPage)}
                onPageSizeChange={(newPageSize: number) => { setPageSize(newPageSize); setPage(0); }}
                getRowId={(row: any) => row._id || Math.random().toString()}
                loading={status === ApiStatus.LOADING}
              />
            </div>
          )}
        </div>
      </Box>
    </Box>

      {/* Salary Form Drawer */}
      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        open={openSalaryDrawer}
        variant="persistent"
        PaperProps={{ sx: { width: 'calc(100vw - 80px)', left: 80, top: 64, height: 'calc(100vh - 64px)', overflow: 'hidden', p: 0 } }}
      >
        <DialogProcessSalaryForm
          title="Process Monthly Salary"
          open={openSalaryDrawer}
          onClose={handleCloseSalary}
        />
      </Drawer>

      {/* Daily Wage Form Drawer */}
      <Drawer
        disableEscapeKeyDown={false}
        anchor="right"
        open={openDailyWageDrawer}
        variant="persistent"
        PaperProps={{ sx: { width: 'calc(100vw - 80px)', left: 80, top: 64, height: 'calc(100vh - 64px)', overflow: 'hidden', p: 0 } }}
      >
        <DialogProcessDailyWageForm
          title="Process Daily Wage"
          open={openDailyWageDrawer}
          onClose={handleCloseDailyWage}
        />
      </Drawer>

      {/* Modern Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: {
            enter: 400,
            exit: 200,
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
            transform: deleteDialogOpen ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            transition: 'all 0.3s ease-in-out',
          },
          '& .MuiDialog-paper': {
            animation: deleteDialogOpen ? 'slideInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
          },
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(60px) scale(0.9)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0) scale(1)',
              opacity: 1,
            },
          },
        }}
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            backgroundColor: '#fef2f2',
            borderBottom: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 3,
            px: 3
          }}
        >
          <Box sx={{
            backgroundColor: '#fee2e2',
            borderRadius: '50%',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DeleteIcon sx={{ color: '#dc2626', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{
              fontWeight: 600,
              color: '#1f2937',
              fontSize: '1.25rem',
              lineHeight: 1.2
            }}>
              Delete {salaryToDelete?.paymentType === 'MONTHLY_SALARY' ? 'Salary' : 'Daily Wage'} Payment
            </Typography>
            <Typography variant="body2" sx={{
              color: '#6b7280',
              mt: 0.5,
              fontSize: '0.875rem'
            }}>
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 4, px: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: '#1f2937',
              fontSize: '1.125rem',
              fontWeight: 400,
              lineHeight: 1.7,
              letterSpacing: '0.01em',
              pt: 2,
              pb: 1,
              mb: 3
            }}
          >
            Are you sure you want to delete this {salaryToDelete?.paymentType === 'MONTHLY_SALARY' ? 'salary' : 'daily wage'} payment record?
          </Typography>
          {salaryToDelete && (
            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                fontSize: '0.95rem',
                lineHeight: 1.6,
                mb: 3
              }}
            >
              Payment Amount: <strong>{Utils.formatNumber(parseFloat(salaryToDelete.netSalary as any) || 0, true, true)}</strong><br />
              Employee: <strong>{salaryToDelete.employee?.name || '-'}</strong>
            </Typography>
          )}

          <Box sx={{
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: 2,
            p: 3,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2
          }}>
            <Box sx={{
              backgroundColor: '#fbbf24',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 24,
              height: 24
            }}>
              <Typography sx={{
                color: '#ffffff',
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}>
                !
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{
                fontWeight: 600,
                color: '#92400e',
                fontSize: '0.875rem',
                mb: 0.5
              }}>
                Important Warning
              </Typography>
              <Typography variant="body2" sx={{
                color: '#92400e',
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}>
                This salary payment and all associated breakdown data will be permanently removed. This data cannot be recovered once deleted.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          backgroundColor: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
          px: 3,
          py: 2.5,
          gap: 1.5
        }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              minWidth: 100,
              height: 40,
              textTransform: 'none',
              fontWeight: 500,
              borderColor: '#d1d5db',
              color: '#374151',
              '&:hover': {
                borderColor: '#6b7280',
                backgroundColor: '#e5e7eb',
                color: '#1f2937'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
            sx={{
              minWidth: 140,
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#dc2626',
              '&:hover': {
                backgroundColor: '#b91c1c'
              },
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
            }}
          >
            Delete Payment
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalaryList;
